import React from 'react';
import styled from 'styled-components';
import { DeemphasizedBadge } from '../../../components/Badge';
import { Card } from '../../../components/Layout';
import { ProgressBar } from '../../../components/ProgressBar';
import { Border, Breakpoints, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { getScoreLevel, SCORE_COLORS, type ScoreLevel } from '../utils/scoreUtils';
import type { CategoryBreakdown, CategoryQuestion } from '../models/assessmentReportView';

const NAVBAR_STICKY_OFFSET = '60px';

const CategoryCard = styled(Card)<{ $isExpanded: boolean }>`
  flex-direction: column;
  padding: 0;
  cursor: default;
  overflow: ${props => (props.$isExpanded ? 'visible' : 'hidden')};
  background-color: ${Color.white};

  &:hover:not(.disabled) {
    box-shadow: ${Shadow.small};

    h4 {
      text-decoration: none;
    }
  }
`;

const CategorySummary = styled.button<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${Grid._4};
  width: 100%;
  padding: ${Grid._5};
  border: none;
  background: ${Color.white};
  text-align: left;
  cursor: pointer;

  ${props =>
    props.$isExpanded &&
    `
    position: -webkit-sticky;
    position: sticky;
    top: ${NAVBAR_STICKY_OFFSET};
    z-index: 10;
    border-bottom: 1px solid ${Color.Gray._20};
    box-shadow: ${Shadow.small};
  `}
`;

const CategoryChevron = styled.i<{ $expanded: boolean }>`
  flex-shrink: 0;
  color: ${Color.Gray._50};
  font-size: ${Type.Scale._2};
  transition: transform 0.2s ease;
  transform: rotate(${props => (props.$expanded ? '90deg' : '0deg')});
`;

const CategoryQuestions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  padding: ${Grid._2};
  border-top: 1px solid ${Color.Gray._20};

  @media (min-width: ${Breakpoints.screen_sm}) {
    padding: ${Grid._5};
  }
`;

const QuestionCard = styled.div`
  flex-direction: column;
  padding: ${Grid._3};
  cursor: default;
  border-bottom: 1px solid ${Color.Gray._20};
  padding-bottom: ${Grid._8};

  &:last-child {
    border-bottom: none;
    padding-bottom: ${Grid._3};
  }

  @media (min-width: ${Breakpoints.screen_sm}) {
    padding: ${Grid._5};
    padding-bottom: ${Grid._8};

    &:last-child {
      padding-bottom: ${Grid._5};
    }
  }
`;
const QuestionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Grid._4};
  margin-bottom: ${Grid._5};
`;

const QuestionTitle = styled.h4`
  margin: 0;
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: ${Type.Leading.tall};
`;

const QuestionScoreSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    align-items: flex-end;
    text-align: right;
  }
`;

const QuestionScore = styled.span`
  font-size: ${Type.Scale._4};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  white-space: nowrap;

  @media (min-width: ${Breakpoints.screen_sm}) {
    font-size: ${Type.Scale._5};
  }
`;

const ResponseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._2};
`;
const ResponseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto;
  gap: ${Grid._2} ${Grid._4};
  align-items: center;
  padding-bottom: ${Grid._4};

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const ResponseLabel = styled.span`
  grid-column: 1;
  font-size: ${Type.Scale._2};
  color: ${Type.Color.dark};
`;

const ResponseStats = styled.div`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  align-items: baseline;
  gap: ${Grid._3};
  white-space: nowrap;
`;

const ResponseCount = styled.span`
  font-size: ${Type.Scale._1};
  color: ${Type.Color.medium};
`;

const ResponsePercent = styled.span`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const ResponseBarFill = styled(ProgressBar)`
  grid-column: 1 / -1;
  height: ${Grid._3};

  &:before {
    height: ${Grid._3};
  }
`;

const CategoryContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CategoryTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Grid._3};
  margin-bottom: ${Grid._2};
`;

const CategoryTitle = styled.h4`
  margin: 0;
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
`;

const CategoryDescription = styled.p`
  margin: 0;
  font-size: ${Type.Scale._2};
  color: ${Type.Color.medium};
  line-height: ${Type.Leading.taller};
`;

const CategoryStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._1};
  flex-shrink: 0;
  font-size: ${Type.Scale._05};
  color: ${Type.Color.medium};
  text-align: right;
  white-space: nowrap;

  @media (min-width: ${Breakpoints.screen_sm}) {
    font-size: ${Type.Scale._1};
  }
`;

const CategoryScoreBox = styled.div<{ $level: ScoreLevel }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${Grid._10};
  height: ${Grid._10};
  border-radius: ${Border.radius};
  background: ${props => SCORE_COLORS[props.$level]};
  color: ${Color.white};
  font-size: ${Type.Scale._5};
  font-weight: ${Type.Weight.bold};
`;

const CategoryContentRight = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  gap: ${Grid._1};

  @media (min-width: ${Breakpoints.screen_sm}) {
    flex-direction: row;
    align-items: center;
    gap: ${Grid._3};
  }
`;

export const AssessmentReportQuestionList = ({ questions }: { questions: CategoryQuestion[] }) => (
  <>
    {questions.map((question, index) => (
      <QuestionCard key={question.text}>
        <QuestionHeader>
          <QuestionTitle>
            {index + 1}. {question.text}
          </QuestionTitle>
          <QuestionScoreSummary>
            <QuestionScore>{question.score.toFixed(2)} / 5</QuestionScore>
          </QuestionScoreSummary>
        </QuestionHeader>
        <ResponseList>
          {question.responses.map(response => (
            <ResponseRow key={response.label}>
              <ResponseLabel>{response.label}</ResponseLabel>
              <ResponseStats>
                <ResponseCount>
                  {response.count} response{response.count === 1 ? '' : 's'}
                </ResponseCount>
                <ResponsePercent>{response.percent}%</ResponsePercent>
              </ResponseStats>
              <ResponseBarFill progress={response.percent} trackColor={Color.Gray._20} fillColor={Color.Blue._50} />
            </ResponseRow>
          ))}
        </ResponseList>
      </QuestionCard>
    ))}
  </>
);

type AssessmentReportCategoryItemProps = {
  category: CategoryBreakdown;
  isExpanded: boolean;
  onToggle: () => void;
};

export const AssessmentReportCategoryItem = ({ category, isExpanded, onToggle }: AssessmentReportCategoryItemProps) => {
  const scoreLevel = getScoreLevel(category.score);

  return (
    <CategoryCard $isExpanded={isExpanded}>
      <CategorySummary
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.title}`}
        $isExpanded={isExpanded}
      >
        <CategoryChevron className="fas fa-chevron-right" $expanded={isExpanded} aria-hidden="true" />
        <CategoryContent>
          <CategoryTitleRow>
            <CategoryTitle>{category.title}</CategoryTitle>
            <DeemphasizedBadge>
              {category.questionCount} question{category.questionCount === 1 ? '' : 's'}
            </DeemphasizedBadge>
          </CategoryTitleRow>
          <CategoryDescription>{category.description}</CategoryDescription>
        </CategoryContent>
        <CategoryContentRight>
          <CategoryStats>
            <span>{category.idealPercent}% ideal</span>
            <span>{category.positivePercent}% positive</span>
          </CategoryStats>
          <CategoryScoreBox $level={scoreLevel}>{category.score.toFixed(2)}</CategoryScoreBox>
        </CategoryContentRight>
      </CategorySummary>
      {isExpanded && (
        <CategoryQuestions>
          <AssessmentReportQuestionList questions={category.questions} />
        </CategoryQuestions>
      )}
    </CategoryCard>
  );
};

export default AssessmentReportCategoryItem;
